import React, { useCallback, useEffect, useState } from 'react';
import { FaArrowLeft, FaCamera, FaKeyboard } from 'react-icons/fa';
import card from '../../assets/card.png';
import faceDetection from '../../assets/faceDetection.png';
import { useAuth } from '../../contexts/authContext';
import { useSteps } from '../../contexts/stepContext';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import { Title } from '../../styles/global';
import { Content, GoBack } from '../BodyStep/styles';
import { ChooseContainer } from './styles';

const Choose: React.FC = () => {
  const {
    handleChangeCurrentStep,
    handleChangeCurrentValidation,
    handleReturnCurrentStep,
    setSkipPhotoRequested,
    photoRequired,
  } = useSteps();

  const { canSelectServices } = useAuth();
  const { translationPage } = useAppTranslation();
  const { tp } = translationPage(tr => tr.components.choose);

  // A escolha é em dois níveis: primeiro foto x preenchimento de dados, e só
  // quem escolhe foto vê os tipos de foto (CNH/Passaporte x face).
  //
  // Com biometria facial no fluxo o primeiro nível não existe: a foto é
  // obrigatória (é ela que a biometria compara), então oferecer "preenchimento
  // de dados" ali só permitiria montar um atendimento que a biometria não
  // consegue concluir. Nesse caso a etapa abre já no segundo nível.
  //
  // Derivado, não estado inicial: `photoRequired` depende do /services, que
  // pode terminar de carregar (ou mudar, se o atendente voltar e trocar os
  // serviços) depois desta tela montar — semear o useState com ele congelaria
  // o valor do primeiro render.
  const [photoOptionsRequested, setPhotoOptionsRequested] = useState(false);
  const showPhotoOptions = photoRequired || photoOptionsRequested;

  const handleSetCNHValidation = useCallback(() => {
    handleChangeCurrentValidation('CNH');
    handleChangeCurrentStep();
  }, [handleChangeCurrentValidation, handleChangeCurrentStep]);

  const handleSetFotoValidation = useCallback(() => {
    handleChangeCurrentValidation('Foto');
    handleChangeCurrentStep();
  }, [handleChangeCurrentValidation, handleChangeCurrentStep]);

  // Não é "pular a foto" — é uma análise que dispensa foto, baseada só nos
  // dados preenchidos.
  const handleSetDataValidation = useCallback(() => {
    setSkipPhotoRequested(true);
    // 'Dados' só identifica o caminho sem foto (o resto do fluxo já trata
    // qualquer valor diferente de 'Foto'/'CNH' da mesma forma).
    handleChangeCurrentValidation('Dados');
    handleChangeCurrentStep();
  }, [
    setSkipPhotoRequested,
    handleChangeCurrentValidation,
    handleChangeCurrentStep,
  ]);

  // Ao (re)entrar nesta etapa, desfaz o método escolhido antes — é o que faz o
  // indicador recolher as etapas seguintes quando o atendente volta até aqui.
  useEffect(() => {
    handleChangeCurrentValidation('');
    setSkipPhotoRequested(false);
  }, [handleChangeCurrentValidation, setSkipPhotoRequested]);

  // Só há para onde voltar DENTRO da etapa quando o atendente mesmo abriu os
  // tipos de foto. Quando a foto é obrigatória o primeiro nível nunca existiu,
  // então "voltar" sai da etapa.
  const canGoBackWithinStep = photoOptionsRequested && !photoRequired;

  const handleBack = useCallback(() => {
    if (canGoBackWithinStep) {
      setPhotoOptionsRequested(false);
      return;
    }
    handleReturnCurrentStep();
  }, [canGoBackWithinStep, handleReturnCurrentStep]);

  return (
    <Content>
      {(canGoBackWithinStep || canSelectServices) && (
        <GoBack onClick={handleBack}>
          <FaArrowLeft size={16} />
          {tp('back')}
        </GoBack>
      )}
      <Title>{tp('title')}</Title>
      <ChooseContainer>
        {showPhotoOptions ? (
          <>
            <button onClick={handleSetCNHValidation} type="button">
              <img src={card} alt="" /> {tp('cnhOption')}
            </button>
            <button onClick={handleSetFotoValidation} type="button">
              <img src={faceDetection} alt="" /> {tp('faceOption')}
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => setPhotoOptionsRequested(true)}
              type="button"
            >
              <FaCamera size={40} /> {tp('photoOption')}
            </button>
            <button onClick={handleSetDataValidation} type="button">
              <FaKeyboard size={40} /> {tp('dataOption')}
            </button>
          </>
        )}
      </ChooseContainer>
    </Content>
  );
};

export default Choose;
